<template>
  <StepDescriptionModal
    :open="isStepDescriptionModalOpen"
    :step-name="purchaseFlowStore.currentStep?.name"
    :substep-name="
      purchaseFlowStore.currentStep?.sub_steps?.[purchaseFlowStore.current_substep - 1]
    "
    @close-modal="isStepDescriptionModalOpen = false"
  />
  <ChangeInvestmentMetricsModal
    class="lg:hidden"
    :open="isChangeInvestmentMetricsModalOpen"
    @close-modal="isChangeInvestmentMetricsModalOpen = false"
  />

  <div class="flex flex-col gap-6 lg:mt-6">
    <!-- Top Section w/ Back CTA and Change Investment Metric CTA's -->
    <div class="flex items-center justify-between">
      <div class="flex gap-4 items-center">
        <Button @click="$emit('back')" size="sm" variant="paper-primary">
          <template #icon>
            <Icon name="ChevronLeft" class="h-4 w-4" :stroke-width="3" />
          </template>
        </Button>
        <div class="flex items-center gap-2 h-6 rounded-[117px] bg-navbar-invest-why-info px-4">
          <Icon name="Save" class="h-4 w-4 text-strong" />
          <p class="text-strong font-medium font-inter text-sm whitespace-nowrap">
            Your Progress is being saved
          </p>
        </div>
      </div>
      <ControlCenterChangeInvestment
        class="hidden lg:flex"
        :class="areChangeMetricsHidden ? '!hidden' : ''"
      />
    </div>

    <div class="flex items-center justify-between">
      <div class="flex flex-col lg:gap-2">
        <p class="text-strongest text-sm md:text-base font-medium font-inter">
          Step {{ purchaseFlowStore?.current_step }}
          <span class="text-strong">of {{ purchaseFlowStore?.investment_steps?.length }}</span>
        </p>
        <p class="text-2xl lg:text-4xl tracking-[-.72px] font-medium text-strongest leading-8">
          {{ stepLabel }}
        </p>
      </div>
      <div class="items-center gap-2 hidden lg:flex">
        <Button @click="isStepDescriptionModalOpen = true" size="lg" variant="paper-primary">
          <template #icon>
            <Icon name="CircleQuestionMark" class="h-6 w-6" />
          </template>
        </Button>
        <Button @click="$emit('continue')" size="lg" variant="primary-secondary-neo">
          <template #label>
            <p class="font-league">{{ continueButtonText }}</p>
          </template>
        </Button>
      </div>
    </div>
  </div>
  <!-- Mobile Fixed Bottom Bar -->
  <div
    v-if="isMakeAnInvestmentRoute"
    class="fixed bottom-0 left-0 right-0 lg:hidden btn-bg-swap bg-purchase-flow-bottom-nav-gradient p-3 flex flex-col justify-center rounded-t-3xl z-30"
    :class="areChangeMetricsHidden ? 'min-h-[70px]' : 'min-h-[115px]'"
    :style="{ paddingBottom: `calc(0.75rem + env(safe-area-inset-bottom))` }"
  >
    <!-- Investment Metrics Summary Row (only on steps where change metrics are visible) -->
    <div v-if="!areChangeMetricsHidden" class="flex items-center justify-between mb-3">
      <div
        class="flex items-center gap-2 text-sm font-inter font-medium text-strongest overflow-x-auto"
      >
        <span class="whitespace-nowrap font-inter">
          ${{ commaSeparateThousands(parseAmount(purchaseFlowStore.current_investment?.amount)) }}
        </span>
        <span class="text-subtle">·</span>
        <span class="whitespace-nowrap font-inter">
          {{
            purchaseFlowStore.current_investment?.is_compounded
              ? "Interest paid at maturity"
              : "Interest paid monthly"
          }}
        </span>
        <span v-if="!isTermAndYieldStep" class="text-subtle">·</span>
        <span class="whitespace-nowrap font-inter" v-if="!isTermAndYieldStep">
          {{ getOfferingTermAndInterestRateDisplay(purchaseFlowStore.selected_offering) }}
        </span>
      </div>
      <Button @click="isChangeInvestmentMetricsModalOpen = true" size="xs" variant="paper-primary">
        <template #icon>
          <Icon name="Pencil" class="h-3.5 w-3.5" :stroke-width="3" />
        </template>
      </Button>
    </div>

    <!-- Continue & Help Row -->
    <div class="flex items-center justify-between gap-2">
      <Button @click="isStepDescriptionModalOpen = true" variant="paper-primary">
        <template #icon>
          <Icon name="CircleQuestionMark" class="h-6 w-6" />
        </template>
      </Button>
      <Button
        @click="$emit('continue')"
        variant="primary-secondary-neo"
        class="w-full sm:w-auto max-w-[400px]"
      >
        <template #label>
          <p class="font-league text-xl">
            {{ continueButtonText }}
          </p>
        </template>
      </Button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";

import { usePurchaseFlowStore } from "@/stores";
import { Button, Icon } from "@/components";
import { commaSeparateThousands } from "@/utilities";
import StepDescriptionModal from "./StepDescriptionModal.vue";
import ChangeInvestmentMetricsModal from "./ChangeInvestmentMetricsModal.vue";
import ControlCenterChangeInvestment from "./ControlCenterChangeInvestment.vue";
import { useOfferingExperiment } from "@/composables/useOfferingExperiment";

const purchaseFlowStore = usePurchaseFlowStore();
const route = useRoute();
const router = useRouter();
const experiment = useOfferingExperiment();

defineEmits(["continue", "back"]);

const isMakeAnInvestmentRoute = computed(() => route.path === "/make-an-investment");

const isStepDescriptionModalOpen = ref(false);
const isChangeInvestmentMetricsModalOpen = ref(false);
const isMobile = ref(false);

onMounted(() => {
  updateIsMobile();
  window.addEventListener("resize", updateIsMobile);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateIsMobile);
});

const isTermAndYieldStep = computed(() => {
  return purchaseFlowStore.currentStep?.name === "Term and Yield";
});

const stepLabel = computed(() => {
  const defaultLabel =
    purchaseFlowStore?.investment_steps[purchaseFlowStore?.current_step - 1]?.label;
  if (experiment.isInTestGroup.value && purchaseFlowStore.currentStep?.name === "Amount") {
    return "Welcome to Phoenix Energy";
  }
  return defaultLabel;
});

const areChangeMetricsHidden = computed(() => {
  // When are the change investment metrics hidden?
  const isFinalize = purchaseFlowStore.currentStep?.name === "Finalize Investment";

  // Before Personal Step
  if (purchaseFlowStore.current_step < 3) return true;

  // If Finalize Investment step, only show on mobile
  if (isFinalize) return !isMobile.value;

  return false;
});

const continueButtonText = computed(() => {
  if (purchaseFlowStore.current_step < purchaseFlowStore.investment_steps.length) return "Continue";
  if (purchaseFlowStore.suitability_issue_state === "has_issues") {
    return isMobile.value ? "Complete review and finish" : "Review and Finish";
  }
  return "Complete Investment";
});

function updateIsMobile() {
  isMobile.value = window.matchMedia("(max-width: 1023px)").matches; // < lg
}

function parseAmount(value) {
  if (!value) return 0;
  return parseInt(value.toString().replace(/[^0-9]/g, "")) || 0;
}
//hi

function getOfferingTermAndInterestRateDisplay(offering) {
  if (!offering) return "";

  const term =
    offering.bond_term >= 12 ? `${offering.bond_term / 12}-year` : `${offering.bond_term}-month`;

  return `${offering.interest_rate}% interest`;
}
</script>
